import React, { useState, useEffect, useRef } from 'react';
import { useChat, useRoomContext, useLocalParticipant } from '@livekit/components-react';
import { motion, AnimatePresence } from 'framer-motion';
import './MiraChatInterface.css';

/**
 * MiraChatInterface - 2025 UX Text Chat for Classroom Mode
 * Design inspired by Discord, iMessage, and Google Messages
 *
 * Features:
 * - Modern bubble-style messages with smooth animations
 * - Auto-expanding input with keyboard shortcuts
 * - Quick action buttons with haptic-style feedback
 * - Typing indicators and connection status
 * - Accessible, responsive, and polished
 */
function MiraChatInterface({ classroomSessionId, onActivityUpdate }) {
  // LiveKit hooks
  const { chatMessages, send, isSending } = useChat();
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();

  // State
  const [input, setInput] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [displayMessages, setDisplayMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);

  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Auto-scroll to bottom with smooth behavior
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [displayMessages]);

  // Check connection status
  useEffect(() => {
    if (room && room.state === 'connected') {
      setIsConnected(true);
      // Welcome message with modern styling
      setDisplayMessages([{
        id: 'welcome',
        role: 'mira',
        text: "Hi! I'm here to help you learn. Ask me anything when you get stuck!",
        timestamp: Date.now()
      }]);
    }
  }, [room]);

  // Process incoming chat messages
  useEffect(() => {
    if (!chatMessages || chatMessages.length === 0) return;

    // Convert LiveKit chat messages to display format
    const formattedMessages = chatMessages.map((msg) => {
      // Determine if message is from Mira (agent) or student
      const isFromAgent = msg.from?.identity?.includes('agent') ||
                          msg.from?.identity?.includes('Mira') ||
                          msg.from?.identity === 'mira';

      return {
        id: `${msg.timestamp}-${msg.from?.identity}`,
        role: isFromAgent ? 'mira' : 'student',
        text: msg.message,
        timestamp: msg.timestamp,
        from: msg.from?.identity
      };
    });

    // Update display messages
    setDisplayMessages(prev => {
      const welcomeMsg = prev.find(m => m.id === 'welcome');
      const newMessages = welcomeMsg ? [welcomeMsg, ...formattedMessages] : formattedMessages;
      return newMessages;
    });

    // Show typing indicator when Mira is generating response
    const latestMessage = formattedMessages[formattedMessages.length - 1];
    if (latestMessage && latestMessage.role === 'student') {
      setIsTyping(true);
      // Clear typing after receiving Mira's response
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 3000);
    } else if (latestMessage && latestMessage.role === 'mira') {
      setIsTyping(false);
    }

    // Log activity
    if (latestMessage && latestMessage.role === 'mira' && onActivityUpdate && classroomSessionId) {
      onActivityUpdate({
        type: 'mira_response',
        sessionId: classroomSessionId,
        messageId: latestMessage.id,
        timestamp: latestMessage.timestamp
      });
    }

  }, [chatMessages, classroomSessionId, onActivityUpdate]);

  // Send message
  const sendMessage = async () => {
    if (!input.trim() || !isConnected || isSending) return;

    // Send directly to lk.chat topic (required for agents to receive)
    try {
      if (room && room.localParticipant) {
        const encoder = new TextEncoder();
        const data = encoder.encode(input.trim());
        // publishData(data, kind, options) - kind: 0=RELIABLE, 1=LOSSY
        await room.localParticipant.publishData(data, 0, { topic: 'lk.chat' });
      }
    } catch (error) {
      console.error('Error sending to lk.chat:', error);
    }

    // Also use useChat send for display
    try {
      await send(input.trim());

      // Log activity
      if (onActivityUpdate && classroomSessionId) {
        onActivityUpdate({
          type: 'student_message',
          sessionId: classroomSessionId,
          text: input.trim(),
          timestamp: Date.now()
        });
      }

      setInput('');
      inputRef.current?.focus();
    } catch (error) {
      console.error('❌ Error sending message:', error);
      console.error('Error details:', { name: error.name, message: error.message, stack: error.stack });
    }
  };

  // Quick actions with smooth UX
  const sendQuickMessage = async (message) => {
    if (!isConnected || isSending) return;
    try {
      await send(message);
      setIsTyping(true);
    } catch (error) {
      console.error('Error sending quick message:', error);
    }
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    // Enter to send (Shift+Enter for newline)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
    // Cmd/Ctrl + K for hint (power user shortcut)
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      sendQuickMessage("Can you give me a hint?");
    }
  };

  // Auto-expand textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
    }
  }, [input]);

  return (
    <div className="mira-chat-2025">
      {/* Header - minimal and modern */}
      <div className="mira-chat-header">
        <div className="mira-header-content">
          <div className="mira-avatar">
            <span className="mira-avatar-emoji">🤖</span>
            {isConnected && <div className="status-indicator online" />}
          </div>
          <div className="mira-info">
            <h3 className="mira-name">Mira</h3>
            <span className={`mira-status ${isConnected ? 'connected' : 'connecting'}`}>
              {isConnected ? 'Active' : 'Connecting...'}
            </span>
          </div>
        </div>
      </div>

      {/* Messages - scrollable area with smooth animations */}
      <div className="mira-messages-container">
        <div className="mira-messages">
          <AnimatePresence initial={false}>
            {displayMessages.map((msg, index) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className={`message-wrapper ${msg.role}`}
              >
                <div className={`message-bubble ${msg.role}`}>
                  <div className="message-text">{msg.text}</div>
                  <div className="message-meta">
                    <span className="message-time">
                      {new Date(msg.timestamp).toLocaleTimeString([], {
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Typing indicator - modern pulsing dots */}
          {isTyping && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="typing-indicator"
            >
              <div className="typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span className="typing-text">Mira is thinking...</span>
            </motion.div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Quick Actions - modern pill buttons */}
      <div className="mira-quick-actions">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => sendQuickMessage("Can you give me a hint?")}
          disabled={!isConnected || isSending}
          className="quick-btn hint"
        >
          <span className="btn-icon">💡</span>
          <span className="btn-text">Hint</span>
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => sendQuickMessage("I got it, thanks!")}
          disabled={!isConnected || isSending}
          className="quick-btn success"
        >
          <span className="btn-icon">✓</span>
          <span className="btn-text">I Got It!</span>
        </motion.button>
      </div>

      {/* Input Area - modern, auto-expanding with smooth focus states */}
      <div className="mira-input-container">
        <div className="mira-input-wrapper">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isConnected ? "Ask Mira for help..." : "Connecting to Mira..."}
            disabled={!isConnected}
            className="mira-input"
            rows={1}
            maxLength={1000}
          />
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={sendMessage}
            disabled={!isConnected || isSending || !input.trim()}
            className="mira-send-btn"
          >
            {isSending ? (
              <div className="spinner" />
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </motion.button>
        </div>
        <div className="mira-input-hint">
          <span className="hint-text">Press Enter to send • Shift+Enter for new line</span>
        </div>
      </div>

      {/* Connection warning - modern toast style */}
      <AnimatePresence>
        {!isConnected && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="connection-warning"
          >
            <div className="warning-icon">⏳</div>
            <div className="warning-text">Connecting to Mira...</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default MiraChatInterface;
